import { useState } from 'react'
import api from '../services/api'
import { useEmpresa } from '../contexts/EmpresaContext'
import LookupSelect from '../components/LookupSelect'

interface ItemNota {
  itsCod: number
  descricao: string
  quantidade: number
  valorUnitario: number
  valorTotal: number
}

interface Nota {
  empcod: number
  serie: string
  numero: number
  ftvTipdco: string
  dataEmissao: string | null
  dataRecebimento: string | null
  cliCod: string
  fornecedor: string
  naturezaDes: string
  usuario: string
  valorMercadoria: number
  valorTotal: number
  itens?: ItemNota[]
}

function hojeISO() { return new Date().toISOString().slice(0, 10) }
function anoAtrasISO() {
  const d = new Date()
  d.setFullYear(d.getFullYear() - 1)
  return d.toISOString().slice(0, 10)
}
function trintaDiasAtrasISO() {
  const d = new Date()
  d.setDate(d.getDate() - 30)
  return d.toISOString().slice(0, 10)
}
function fmtData(v: string | null) { return v ? v.slice(0, 10).split('-').reverse().join('/') : '-' }
function fmtMoeda(v: number) { return (v || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) }

const inp: React.CSSProperties = {
  border: '1px solid #d1d5db', borderRadius: 4, padding: '4px 8px',
  fontSize: '0.85rem', height: 32, background: '#fff',
}
const label: React.CSSProperties = { fontSize: '0.72rem', color: '#6b7280', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.03em' }

function Campo({ rotulo, width, children }: { rotulo: string; width?: number; children: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 3, width }}>
      <label style={label}>{rotulo}</label>
      {children}
    </div>
  )
}

export default function RelatorioNotasComprasRecebimentoEmissao() {
  const { empresaCod } = useEmpresa()
  const [empcodDe, setEmpcodDe] = useState(String(empresaCod ?? 1))
  const [empcodAte, setEmpcodAte] = useState(String(empresaCod ?? 1))
  const [dataEmissaoDe, setDataEmissaoDe] = useState(anoAtrasISO())
  const [dataEmissaoAte, setDataEmissaoAte] = useState(hojeISO())
  const [dataRecebimentoDe, setDataRecebimentoDe] = useState(trintaDiasAtrasISO())
  const [dataRecebimentoAte, setDataRecebimentoAte] = useState(hojeISO())
  const [classificacaoPor, setClassificacaoPor] = useState<'recebimento' | 'emissao'>('recebimento')
  const [contaCod, setContaCod] = useState('')
  const [contaDes, setContaDes] = useState('')
  const [fornecedorCod, setFornecedorCod] = useState('')
  const [fornecedorDes, setFornecedorDes] = useState('')
  const [usuario, setUsuario] = useState('')
  const [tipoRelatorio, setTipoRelatorio] = useState<'resumido' | 'detalhado'>('resumido')
  const [quebraPorData, setQuebraPorData] = useState(false)

  const [carregando, setCarregando] = useState(false)
  const [gerandoPdf, setGerandoPdf] = useState(false)
  const [gerandoExcel, setGerandoExcel] = useState(false)
  const [erro, setErro] = useState('')
  const [notas, setNotas] = useState<Nota[] | null>(null)

  function filtrosParams() {
    return {
      empcodDe: empcodDe || undefined, empcodAte: empcodAte || undefined,
      dataEmissaoDe: dataEmissaoDe || undefined, dataEmissaoAte: dataEmissaoAte || undefined,
      dataRecebimentoDe: dataRecebimentoDe || undefined, dataRecebimentoAte: dataRecebimentoAte || undefined,
      classificacaoPor, contaFinanceira: contaCod || undefined, fornecedorCod: fornecedorCod || undefined,
      usuario: usuario || undefined, tipoRelatorio,
    }
  }

  async function gerar() {
    setCarregando(true); setErro(''); setNotas(null)
    try {
      const r = await api.get('/relatorios-compras/notas-recebimento-emissao', { params: filtrosParams() })
      setNotas(r.data)
    } catch (e: any) {
      setErro(e?.response?.data?.error || 'Erro ao gerar relatório.')
    } finally {
      setCarregando(false)
    }
  }

  async function gerarPdf() {
    setGerandoPdf(true); setErro('')
    try {
      const r = await api.get('/relatorios-compras/notas-recebimento-emissao/pdf', {
        params: { ...filtrosParams(), quebraPorData: quebraPorData ? 'S' : 'N' }, responseType: 'blob',
      })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }))
      window.open(url, '_blank')
    } catch {
      setErro('Erro ao gerar PDF.')
    } finally {
      setGerandoPdf(false)
    }
  }

  async function gerarExcel() {
    setGerandoExcel(true); setErro('')
    try {
      const r = await api.get('/relatorios-compras/notas-recebimento-emissao/excel', { params: filtrosParams(), responseType: 'blob' })
      const url = URL.createObjectURL(new Blob([r.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'notas-recebimento-emissao.xlsx'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setErro('Erro ao gerar Excel.')
    } finally {
      setGerandoExcel(false)
    }
  }

  const totais = (notas ?? []).reduce((acc, n) => {
    acc.mercadoria += n.valorMercadoria
    acc.total += n.valorTotal
    return acc
  }, { mercadoria: 0, total: 0 })

  return (
    <div>
      <h2 style={{ fontSize: '1.1rem', fontWeight: 700, margin: '0 0 0.75rem' }}>
        Notas Fiscais P/data Recebimento / Emissão
      </h2>

      <div style={{
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
        boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem', marginBottom: '1rem',
      }}>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '0.75rem' }}>
          <Campo rotulo="Empresa De" width={90}>
            <input style={inp} inputMode="numeric" value={empcodDe} onChange={e => setEmpcodDe(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Até" width={90}>
            <input style={inp} inputMode="numeric" value={empcodAte} onChange={e => setEmpcodAte(e.target.value.replace(/\D/g, ''))} />
          </Campo>
          <Campo rotulo="Emissão De" width={150}>
            <input style={inp} type="date" value={dataEmissaoDe} onChange={e => setDataEmissaoDe(e.target.value)} />
          </Campo>
          <Campo rotulo="Até" width={150}>
            <input style={inp} type="date" value={dataEmissaoAte} onChange={e => setDataEmissaoAte(e.target.value)} />
          </Campo>
          <Campo rotulo="Recebimento De" width={150}>
            <input style={inp} type="date" value={dataRecebimentoDe} onChange={e => setDataRecebimentoDe(e.target.value)} />
          </Campo>
          <Campo rotulo="Até" width={150}>
            <input style={inp} type="date" value={dataRecebimentoAte} onChange={e => setDataRecebimentoAte(e.target.value)} />
          </Campo>
          <Campo rotulo="Classificação por" width={160}>
            <select style={inp} value={classificacaoPor} onChange={e => setClassificacaoPor(e.target.value as any)}>
              <option value="recebimento">Data Recebimento</option>
              <option value="emissao">Data Emissão</option>
            </select>
          </Campo>
        </div>

        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Campo rotulo="Conta Financeira (opcional)" width={260}>
            <LookupSelect
              endpoint="/erp/contas" codKey="dep_cod" descKey="dep_des"
              codValue={contaCod} descValue={contaDes}
              onSelect={(cod, des) => { setContaCod(cod); setContaDes(des) }}
              placeholder="Buscar conta financeira..." listWidth={320}
            />
          </Campo>
          <Campo rotulo="Fornecedor (opcional)" width={280}>
            <LookupSelect
              endpoint="/erp/clientes/lista" codKey="cli_cod" descKey="cli_fan"
              codValue={fornecedorCod} descValue={fornecedorDes}
              onSelect={(cod, des) => { setFornecedorCod(cod); setFornecedorDes(des) }}
              placeholder="Buscar fornecedor..." listWidth={320} somenteComTexto
            />
          </Campo>
          <Campo rotulo="Usuário (opcional)" width={200}>
            <LookupSelect
              endpoint="/erp/usuarios/lista" codKey="usu_nom" descKey="usu_nom"
              codValue={usuario} descValue={usuario}
              onSelect={(cod) => setUsuario(cod)}
              placeholder="Nulo p/ todos" listWidth={240}
            />
          </Campo>
          <Campo rotulo="Tipo de relatório" width={150}>
            <select style={inp} value={tipoRelatorio} onChange={e => setTipoRelatorio(e.target.value as any)}>
              <option value="resumido">Resumido</option>
              <option value="detalhado">Detalhado (com itens)</option>
            </select>
          </Campo>
          <Campo rotulo="Quebra pág. por Data" width={150}>
            <select style={inp} value={quebraPorData ? 'S' : 'N'} onChange={e => setQuebraPorData(e.target.value === 'S')}>
              <option value="N">Não</option>
              <option value="S">Sim</option>
            </select>
          </Campo>
          <button className="btn btn-primary btn-sm" disabled={carregando} onClick={gerar} style={{ height: 32 }}>
            {carregando ? 'Gerando...' : 'Gerar Relatório'}
          </button>
        </div>
      </div>

      {erro && <div className="alert alert-danger" style={{ marginBottom: '0.75rem' }}>{erro}</div>}

      {notas && (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          boxShadow: '0 1px 4px rgba(0,0,0,0.07)', padding: '1rem 1.1rem',
        }}>
          <div style={{ display: 'flex', gap: 8, marginBottom: '0.9rem' }}>
            <button className="btn btn-sm" disabled={gerandoPdf} onClick={gerarPdf} style={{ height: 32, background: '#dc2626', borderColor: '#dc2626', color: '#fff' }}>
              {gerandoPdf ? 'Gerando...' : '📄 Gerar PDF'}
            </button>
            <button className="btn btn-sm" disabled={gerandoExcel} onClick={gerarExcel} style={{ height: 32, background: '#15803d', borderColor: '#15803d', color: '#fff' }}>
              {gerandoExcel ? 'Gerando...' : '📊 Gerar Excel'}
            </button>
          </div>

          {notas.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>Nenhuma nota encontrada.</div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table className="table" style={{ width: '100%', fontSize: '0.78rem', marginBottom: '0.5rem' }}>
                <thead>
                  <tr className="thead-claro" style={{ background: '#eef2f7' }}>
                    <th style={{ textAlign: 'center', width: 40 }}>Emp</th>
                    <th style={{ textAlign: 'center', width: 90 }}>Emissão</th>
                    <th style={{ textAlign: 'center', width: 90 }}>Recebimento</th>
                    <th style={{ textAlign: 'left' }}>Fornecedor</th>
                    <th style={{ textAlign: 'center', width: 60 }}>Série</th>
                    <th style={{ textAlign: 'center', width: 80 }}>Número</th>
                    <th style={{ textAlign: 'left', width: 220 }}>Natureza</th>
                    <th style={{ textAlign: 'center', width: 110 }}>Usuário</th>
                    <th style={{ textAlign: 'right', width: 110 }}>Vlr. Mercad.</th>
                    <th style={{ textAlign: 'right', width: 110 }}>Vlr. Total</th>
                  </tr>
                </thead>
                <tbody>
                  {notas.map((n, idx) => (
                    <>
                      <tr key={`${n.empcod}-${n.serie}-${n.numero}-${n.cliCod}`} style={{ background: idx % 2 === 1 ? '#f8fafc' : '#fff' }}>
                        <td style={{ textAlign: 'center' }}>{n.empcod}</td>
                        <td style={{ textAlign: 'center' }}>{fmtData(n.dataEmissao)}</td>
                        <td style={{ textAlign: 'center' }}>{fmtData(n.dataRecebimento)}</td>
                        <td style={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={n.fornecedor}>{n.fornecedor}</td>
                        <td style={{ textAlign: 'center' }}>{n.serie}</td>
                        <td style={{ textAlign: 'center', fontWeight: 700, color: '#1a6ea8' }}>{n.numero}</td>
                        <td style={{ maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={n.naturezaDes}>{n.naturezaDes}</td>
                        <td style={{ textAlign: 'center', color: '#6b7280' }}>{n.usuario}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{fmtMoeda(n.valorMercadoria)}</td>
                        <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{fmtMoeda(n.valorTotal)}</td>
                      </tr>
                      {tipoRelatorio === 'detalhado' && n.itens?.map(it => (
                        <tr key={`${n.empcod}-${n.serie}-${n.numero}-${n.cliCod}-${it.itsCod}`} style={{ background: '#fbfcfe' }}>
                          <td></td>
                          <td colSpan={3} style={{ color: '#6b7280', fontStyle: 'italic', fontSize: '0.74rem' }}>
                            ↳ {it.itsCod} — {it.descricao}
                          </td>
                          <td colSpan={2} style={{ color: '#6b7280', fontSize: '0.74rem' }}>Qtd {it.quantidade}</td>
                          <td colSpan={2} style={{ color: '#6b7280', fontSize: '0.74rem' }}>Unit. {fmtMoeda(it.valorUnitario)}</td>
                          <td style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.74rem' }}></td>
                          <td style={{ textAlign: 'right', color: '#6b7280', fontSize: '0.74rem' }}>{fmtMoeda(it.valorTotal)}</td>
                        </tr>
                      ))}
                    </>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ borderTop: '2px solid #cbd5e1', fontWeight: 700 }}>
                    <td colSpan={8} style={{ textAlign: 'right', padding: '6px 8px' }}>TOTAL:</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totais.mercadoria)}</td>
                    <td style={{ textAlign: 'right', padding: '6px 8px' }}>{fmtMoeda(totais.total)}</td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
